import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {DialogService, DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ViewportService} from 'src/app/services/viewport.service';
import {Destination} from '../../../models/AirportModel';
import {AirportsService} from 'src/app/services/airports.service';
import {ToastrService} from 'ngx-toastr';
import {UserModel} from '../../../models/User';
import {DealsService} from '../../../services/deals.service';
import { MixpanelService } from 'src/app/services/mixpanel.service';
import {TierUpgradeMode, UnlockDealModalComponent} from '../unlock-deal-modal/unlock-deal-modal.component';
import {SearchSelectComponent} from '../../shared/search-select-form/search-select-form.component';
import { openModal } from 'src/app/utilities/helpers';
import {RefreshTrigger, RefreshContext} from '../../../services/refresh.service';
import { tap } from 'rxjs';

export interface DreamDestinationModalData {
    destinations: Destination[];
    user: UserModel;
    maxCount: number;
    dealRefreshTrigger?: RefreshTrigger;
}

@Component({
    selector: 'dream-destination-modal',
    templateUrl: './dream-destination-modal.component.html',
    styleUrl: './dream-destination-modal.component.css',
})
export class DreamDestinationModalComponent implements OnInit, OnDestroy {
    destinations: Destination[] = [];
    user: UserModel;
    maxCount: number;
    hoveredDestinationId: number | null = null;
    dealRefreshTrigger?: RefreshTrigger;

    @ViewChild('searchBox') searchBox: SearchSelectComponent;

    @Input() visible = false;
    @Output() onClose = new EventEmitter<void>();

    constructor(
        public ref: DynamicDialogRef,
        public config: DynamicDialogConfig<DreamDestinationModalData>,
        private airportsService: AirportsService,
        private toast: ToastrService,
        public viewport: ViewportService,
        private dealService: DealsService,
        private mixpanel: MixpanelService,
        private dialogService: DialogService
    ) {}

    ngOnInit() {
        this.destinations = [...(this.config.data?.destinations || [])];
        this.user = this.config.data.user;
        this.maxCount = this.config.data.maxCount;
        this.dealRefreshTrigger = this.config.data?.dealRefreshTrigger;
        this.mixpanel.identify(this.user.id.toString());
    }
    ngOnDestroy(): void {}

    hide() {
        this.onClose.emit();
    }
    close() {
        this.ref.close(this.destinations);
    }

    onAddDestination = (dest: Destination) => {
        this.destinations = [dest, ...this.destinations]; 
        this.airportsService.setDestination([{ id: dest.id }]).pipe(
            tap(() => {
                const destinationStateOrCountry = dest.stateName
                    ? dest.stateName
                    : dest.country.name;
                this.mixpanel.track('Destination airport added', {
                    email: this.user.email,
                    destination: `${dest.name}, ${destinationStateOrCountry}`,
                });
            }),
            tap(() => {
                this.dealRefreshTrigger?.refresh(RefreshContext.DestinationModal);
            })
        ).subscribe({
            error: (err) => {
                this.destinations = this.destinations.filter(
                    (d) => d.id !== dest.id
                );
                this.toast.error(
                    'Could not save that destination. Please try again.'
                );
            },
        });
    };

    removeDestination(dest: Destination) {
        this.destinations = this.destinations.filter((d) => d.id !== dest.id); 
        this.airportsService.removeDestination(dest.id).pipe(
            tap(() => {
                const destinationStateOrCountry = dest.stateName
                    ? dest.stateName
                    : dest.country;
                this.mixpanel.track('Destination airport removed', {
                    email: this.user.email,
                    destination: `${dest.name}, ${destinationStateOrCountry}`,
                });
            }),
            tap(() => {
                this.dealRefreshTrigger?.refresh(RefreshContext.DestinationModal);
            })
        ).subscribe({
            error: (err) => {
                this.destinations = [dest, ...this.destinations];
                this.toast.error(
                    'Could not remove that destination. Please Try again'
                );
            },
        });
    }

    searchDestinations = (term: string) =>
        this.airportsService.searchDestinations(term);

    trackByDestId = (_: number, d: Destination) => d.id;

    handleAddDestinationClick(): void {
        if (this.user?.isFree && this.destinations.length >= this.maxCount) {
            this.openUpgradeModal();
        } else {
            this.searchBox.focusInput();
        }
    }

    openUpgradeModal(): void {
        openModal(this.dialogService, UnlockDealModalComponent, {
            data: {
                isVisible: true,
                mode: TierUpgradeMode.moreDestinations,
                parentRef: this.ref,
            },
            styleClass: 'dashboard-modal small-modal'
        });
    }
}
